import React from 'react';
import router from 'next/router';

import { withAuth } from '@/components/with_auth';
import routes from '@/services/routes';
import IconWrapper from '@/components/commons/IconWrapper';

import LandingWithCards from '@/components/layout/LandingWithCards';
import QuestionCircleOutline from '@/components/commons/icons/QuestionCircleOutline';
import CalendarMonthOutline from '@/components/commons/icons/CalendarMonthOutline';
import ProfileCardOutline from '@/components/commons/icons/ProfileCardOutline';
import BellOutline from '@/components/commons/icons/BellOutline';
import MessageCaptionOutline from '@/components/commons/icons/MessageCaptionOutline';

const mainTitle = {
    title: 'AI Assistant',
    subtitle: 'Sixty\'s AI-assisted tools operate in the background, tuning out unnecessary communications and foregrounding what\'s really important.',
};

const bigNavCards = [
    {
        key: 'ai-assistant',
        heading: {
            children: 'What is Sixty\'s AI assistant?',
            bold: true,
        },
        body: {
            text: 'Keep on top of your tasks and in touch with everyone who matters. We\'re like a human helper who makes sure you always know what you\'re supposed to be doing, preps you for your meetings, gets you there on time, and reminds you to say happy birthday to the people you\'re connected to.',
            style: 'my-5',
        },
        link: {
            children: (
                <div className="text-sm text-progressBarBlue font-medium underline flex items-center">
                    <IconWrapper
                        svg={<QuestionCircleOutline className="w-5 h-5 text-cleanSlate" />}
                        className="mr-1"
                    />
                    Get help with AI Assistant features
                </div>
            ),
            url: process.env.NEXT_PUBLIC_HELPSCOUT_ROOT,
        },
        target: '_blank',
        image: {
            src: '/images/calendar/person-waving-inside-phone.png',
            alt: 'Person waving inside phone',
            style: 'sm:max-w-[270px]',
        },
        externalStyles: 'm-2 flexbox gap-4 md:mx-5 md:my-6',
        externalTextBoxStyles: 'md:mb-7',
        externalContainerStyles: 'flex relative items-center flex-wrap md:col-span-2 xl:col-span-3 md:bg-[url("/images/inbox/background-login-big-navigation-card.png")] md:bg-right-bottom md:bg-no-repeat md:bg-[length:65%]',
    },
];

const cards = [
    {
        key: 'agendas',
        image: {
            src: '/images/calendar/person-by-checklist.png',
            style: 'max-h-[100px] max-w-[200px]',
            alt: 'Person by checklist',
        },
        heading: {
            children: (
                <>
                    <IconWrapper
                        svg={<CalendarMonthOutline className="w-5 h-5 dark:text-cirrusWhite" />}
                        className="mr-3"
                    />
                    Agendas
                </>
            ),
            bold: true,
            externalStyles: 'flex my-5 items-center',
        },
        body: {
            text: 'Create, customize, and manage email agendas to prepare yourself for the day or week ahead.',
            style: 'mb-2',
        },
        button: {
            onClick: (_event) => router.push(routes.AGENDAS),
            text: 'Manage Agendas',
            size: 'base',
            type: 'primary',
            style: 'mt-auto',
        },
    },
    {
        key: 'bio',
        image: {
            src: '/images/calendar/bio-page.png',
            style: 'max-h-[100px] max-w-[200px]',
            alt: 'Bio page',
        },
        heading: {
            children: (
                <>
                    <IconWrapper
                        svg={<ProfileCardOutline className="w-5 h-5 dark:text-cirrusWhite" />}
                        className="mr-3"
                    />
                    Brief
                </>
            ),
            bold: true,
            externalStyles: 'flex my-5 items-center',
        },
        body: {
            text: 'Create a shareable brief to maximize the potential of upcoming meetings, and communicate your preferences.',
            style: 'mb-2',
        },
        button: {
            onClick: (_event) => router.push(routes.BIO),
            text: 'Manage Brief',
            size: 'base',
            type: 'primary',
            style: 'mt-auto',
        },
    },
    {
        key: 'alerts',
        image: {
            src: '/images/calendar/person-moving-alerts.png',
            style: 'max-h-[100px] max-w-[200px]',
            alt: 'Person moving alerts',
        },
        heading: {
            children: (
                <>
                    <IconWrapper
                        svg={<BellOutline className="w-5 h-5 dark:text-cirrusWhite" />}
                        className="mr-3"
                    />
                    Smart Alerts
                </>
            ),
            bold: true,
            externalStyles: 'flex my-5 items-center',
        },
        body: {
            text: 'Create and manage personalized notifications to stay on top of your day\'s tasks and events.',
            style: 'mb-2',
        },
        button: {
            onClick: (_event) => router.push(routes.ALERTS),
            text: 'Manage Smart Alerts',
            size: 'base',
            type: 'primary',
            style: 'mt-auto',
        },
    },
    {
        key: 'outreach',
        image: {
            src: '/images/calendar/birthday-balloons.png',
            alt: 'Birthday balloons',
            style: 'max-h-[100px] max-w-[200px]',
        },
        heading: {
            children: (
                <>
                    <IconWrapper
                        svg={<MessageCaptionOutline className="w-5 h-5 dark:text-cirrusWhite" />}
                        className="mr-3"
                    />
                    Outreach
                </>
            ),
            bold: true,
            externalStyles: 'flex my-5 items-center',
        },
        body: {
            text: 'Never lose touch with the people in your network.',
            style: 'mb-2',
        },
        button: {
            onClick: (_event) => router.push(routes.BIRTHDAYS),
            text: 'Manage Outreach',
            size: 'base',
            type: 'primary',
            style: 'mt-auto',
        },
    },
];

function AIAssistant() {
    return (
        <LandingWithCards mainTitle={mainTitle} bigNavCards={bigNavCards} cards={cards} />
    );
}

export default withAuth(AIAssistant);
